import type { Meeting } from '../../../services/meeting.service.ts';
import type { CalendarEvent } from '../types/calendar.types.ts';
import { isMeetingLiveActive } from '../../meeting/utils/meetingLiveExpiry.ts';

function meetingColor(meeting: Meeting): CalendarEvent['color'] {
  if (meeting.status === 'ENDED' || meeting.status === 'CANCELLED') return 'gray';
  if (meeting.meetingFormat === 'IN_PERSON') return 'green';
  if (isMeetingLiveActive(meeting)) return 'amber';
  return 'purple';
}

export function meetingToCalendarEvent(meeting: Meeting): CalendarEvent | null {
  if (meeting.isInstant && !isMeetingLiveActive(meeting)) {
    return null;
  }

  const startRaw =
    meeting.scheduledAt ||
    meeting.startedAt ||
    (meeting.isInstant ? meeting.createdAt : undefined);

  if (!startRaw) return null;

  const start = new Date(startRaw);
  if (Number.isNaN(start.getTime())) return null;

  const durationMinutes = meeting.duration > 0 ? meeting.duration : 60;
  const end = new Date(start.getTime() + durationMinutes * 60_000);

  return {
    id: `tbk_${meeting.id}`,
    source: 'tbkmeet',
    title: meeting.title,
    start,
    end,
    allDay: false,
    meeting,
    location: meeting.location || meeting.room || undefined,
    color: meetingColor(meeting),
  };
}

export function meetingsToCalendarEvents(meetings: Meeting[]): CalendarEvent[] {
  return meetings
    .map(meetingToCalendarEvent)
    .filter((e): e is CalendarEvent => e != null)
    .sort((a, b) => a.start.getTime() - b.start.getTime());
}

export function groupEventsByDate(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();

  for (const event of events) {
    const key = `${event.start.getFullYear()}-${String(event.start.getMonth() + 1).padStart(2, '0')}-${String(event.start.getDate()).padStart(2, '0')}`;
    const list = map.get(key) ?? [];
    list.push(event);
    map.set(key, list);
  }

  for (const [, list] of map) {
    list.sort((a, b) => a.start.getTime() - b.start.getTime());
  }

  return map;
}
